// Substep 5-θ.3 + 5-ι.1: SOP body renderer with inline credential chips
// AND basic markdown (headers / lists / bold / italic / code / links).
//
// Called from three sites: FirmSopsPage viewer modal, TasksCard
// expanded-task linked-SOP viewer, TasksCard firm-handbook viewer.
//
// Pipeline:
//   1. Strip the front-matter I inject at upload time ("# N. Title",
//      "_Category: x_", "_Source: y_", horizontal rule) — the viewer's
//      own header already shows title + category, so re-rendering it
//      inside the body is pure duplicate clutter.
//   2. Split the remaining body line-by-line and classify each line
//      (heading, bullet, numbered, blank, regular).
//   3. Within each line, parse inline: `code`, **bold**, *italic*,
//      [text](url), and {{creds.<service>.<field>}} placeholders.
//      Placeholder = interactive reveal chip (POST /reveal → plaintext
//      + clipboard copy + 30s auto-hide).
//
// Inline parser is a small hand-rolled matcher — no markdown lib.
// Kept intentionally narrow: headers, lists, and the 4 inline flavors
// cover 95% of MCB SOP formatting. Add tables / nested lists / etc.
// only if a real SOP needs them; keeps bundle size + XSS surface
// minimal (no dangerouslySetInnerHTML anywhere).

import React, { useState, useRef, useEffect } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

const _PLACEHOLDER_PATTERN =
  /\{\{\s*creds\.([a-z0-9_]+)\.(username|password|url|notes)\s*\}\}/g;
const _REVEAL_TTL_MS = 30_000;


// ────────────────────────────────────────────────────────────────
// Front-matter strip
// ────────────────────────────────────────────────────────────────

function _stripFrontMatter(text) {
  // Only strip at the top of the body — an "# N. Title" line halfway
  // through a real SOP is content, not a header we injected.
  const lines = text.split("\n");
  const out = [];
  let inFront = true;
  for (const line of lines) {
    if (inFront) {
      const t = line.trim();
      if (!t) continue;                              // leading blanks
      if (/^#\s+\d+\.\s+/.test(t)) continue;         // "# 13. Title"
      if (/^_(Category|Source):.*_\s*$/i.test(t)) continue;
      if (/^-{3,}\s*$/.test(t)) continue;            // "---"
      inFront = false;
    }
    out.push(line);
  }
  // Trim leading empty lines that fall through
  while (out.length && !out[0].trim()) out.shift();
  return out.join("\n");
}


// ────────────────────────────────────────────────────────────────
// Inline parser — bold, italic, code, links, credential chips
// ────────────────────────────────────────────────────────────────

// Order matters: code first so backticks don't get munged by * / **.
// Each entry is { pattern: RegExp with capture, render: (match, ...) => ReactNode }.
// The renderer scans left-to-right, taking the earliest match at each
// step.
const _INLINE_MATCHERS = [
  {
    // Credential placeholder — returned as a special marker; the outer
    // renderer swaps in the actual chip component (needs state + hooks).
    kind: "chip",
    pattern: _PLACEHOLDER_PATTERN,
  },
  {
    // Inline code: `text`
    kind: "code",
    pattern: /`([^`]+)`/g,
  },
  {
    // Bold: **text**
    kind: "bold",
    pattern: /\*\*([^*]+)\*\*/g,
  },
  {
    // Italic: *text* (NOT **text**; skip if preceded/followed by *)
    kind: "italic",
    pattern: /(?<!\*)\*([^*\n]+)\*(?!\*)/g,
  },
  {
    // Link: [text](url)
    kind: "link",
    pattern: /\[([^\]]+)\]\(([^)]+)\)/g,
  },
  {
    // Bare URL — auto-link. Restrict to http(s) to avoid catching
    // random colons.
    kind: "url",
    pattern: /(https?:\/\/[^\s)]+)/g,
  },
];

function _parseInline(text, ctx) {
  // Find the earliest match across all matchers, render it, and
  // recurse on the remaining text. Returns an array of React
  // elements + strings.
  if (!text) return [];
  const out = [];
  let cursor = 0;
  const findNextMatch = (from) => {
    let best = null;
    for (const m of _INLINE_MATCHERS) {
      m.pattern.lastIndex = from;
      const hit = m.pattern.exec(text);
      if (hit && (!best || hit.index < best.match.index)) {
        best = { matcher: m, match: hit };
      }
    }
    return best;
  };

  let next = findNextMatch(cursor);
  let n = 0;
  while (next) {
    const { matcher, match } = next;
    if (match.index > cursor) {
      out.push(text.slice(cursor, match.index));
    }
    if (matcher.kind === "chip") {
      out.push(React.createElement(CredChip, {
        key: `c${n++}`,
        serviceKey: match[1], field: match[2], ctx,
      }));
    } else if (matcher.kind === "code") {
      out.push(React.createElement("code", {
        key: `c${n++}`,
        style: {
          background: C.warmWhite, padding: "1px 5px", borderRadius: "3px",
          fontSize: "0.9em", fontFamily: "monospace",
        }
      }, match[1]));
    } else if (matcher.kind === "bold") {
      out.push(React.createElement("strong", { key: `c${n++}`,
        style: { fontWeight: 700, color: C.navy } }, match[1]));
    } else if (matcher.kind === "italic") {
      out.push(React.createElement("em", { key: `c${n++}` }, match[1]));
    } else if (matcher.kind === "link") {
      out.push(React.createElement("a", {
        key: `c${n++}`, href: match[2], target: "_blank", rel: "noopener noreferrer",
        style: { color: C.navy, textDecoration: "underline" }
      }, match[1]));
    } else if (matcher.kind === "url") {
      out.push(React.createElement("a", {
        key: `c${n++}`, href: match[1], target: "_blank", rel: "noopener noreferrer",
        style: { color: C.navy, textDecoration: "underline", wordBreak: "break-all" }
      }, match[1]));
    }
    cursor = match.index + match[0].length;
    next = findNextMatch(cursor);
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}


// ────────────────────────────────────────────────────────────────
// Line classifier
// ────────────────────────────────────────────────────────────────

// Returns { kind, level?, marker?, content } for a single line.
// Kind ∈ { "heading" | "bullet" | "numbered" | "blank" | "regular" }.
function _classifyLine(line) {
  if (!line.trim()) return { kind: "blank" };
  let m;
  if ((m = /^(#{1,4})\s+(.+)$/.exec(line))) {
    return { kind: "heading", level: m[1].length, content: m[2] };
  }
  if ((m = /^\s*[-*]\s+(.+)$/.exec(line))) {
    return { kind: "bullet", content: m[1] };
  }
  if ((m = /^\s*(\d+)[.)]\s+(.+)$/.exec(line))) {
    return { kind: "numbered", marker: m[1], content: m[2] };
  }
  return { kind: "regular", content: line };
}


// ────────────────────────────────────────────────────────────────
// Chip component — holds its own reveal state + timer
// ────────────────────────────────────────────────────────────────

// Rendered via React.createElement, so it is a component and its
// name has to say so — the hooks lint cannot tell otherwise.
function CredChip({ serviceKey, field, ctx }) {
  const [revealed, setRevealed] = useState(null); // {value, hideAt}
  const [pending, setPending] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const onReveal = async () => {
    if (pending) return;
    setPending(true);
    try {
      const resp = await apiFetch(`/api/firm-credentials/${serviceKey}/reveal`, {
        method: "POST",
        body: JSON.stringify({
          field, matter_id: ctx.matterId ? String(ctx.matterId) : null,
        }),
      });
      const value = resp.value ?? "";
      setRevealed({ value, hideAt: Date.now() + _REVEAL_TTL_MS });
      try {
        if (navigator.clipboard?.writeText && value) {
          await navigator.clipboard.writeText(value);
          ctx.showNotification?.(`${field} copied to clipboard.`, "success");
        }
      } catch {}
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setRevealed(null), _REVEAL_TTL_MS);
    } catch (e) {
      let msg;
      if (e.status === 503) msg = "Vault is disabled — CHARPEN_CREDENTIAL_KEY not set.";
      else if (e.status === 404 || (e.message && e.message.includes("no credential"))) {
        msg = `Credential "${serviceKey}" not set — add it in Firm Settings → Firm Vault.`;
      } else msg = `Reveal failed: ${e.message || e.status}`;
      ctx.showNotification?.(msg, "error");
    }
    setPending(false);
  };

  const onHide = (e) => {
    e.preventDefault();
    setRevealed(null);
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  return React.createElement("span", {
    style: {
      display: "inline-flex", alignItems: "center", gap: "6px",
      padding: "1px 8px", margin: "0 2px",
      borderRadius: "999px",
      border: `1px solid ${revealed ? C.gold : C.warmGray}`,
      background: revealed ? `${C.gold}18` : C.warmWhite,
      color: C.textDark,
      fontFamily: "system-ui, -apple-system, sans-serif",
      fontSize: "11px", fontWeight: 500,
      verticalAlign: "middle",
      userSelect: revealed ? "all" : "none",
    }
  },
    React.createElement("span", { style: { fontSize: "10px", opacity: 0.7 } }, "\u{1F512}"),
    revealed
      ? React.createElement("span", {
          style: { fontFamily: "monospace", color: C.textDark, fontWeight: 600 }
        }, revealed.value)
      : React.createElement("span", { style: { color: C.textMuted } },
          `${serviceKey} · ${field}`),
    revealed
      ? React.createElement("button", {
          onClick: onHide,
          title: "Hide (also auto-hides after 30s)",
          style: {
            border: "none", background: "transparent",
            color: C.textMuted, fontSize: "10px",
            cursor: "pointer", padding: "0 2px",
          }
        }, "Hide")
      : React.createElement("button", {
          onClick: (e) => { e.preventDefault(); onReveal(); },
          disabled: pending,
          title: `Reveal ${field} for ${serviceKey} (audit-logged)`,
          style: {
            border: "none", background: "transparent",
            color: C.gold, fontSize: "10px", fontWeight: 700,
            cursor: pending ? "default" : "pointer",
            padding: "0 2px", textTransform: "uppercase",
            letterSpacing: "0.03em",
          }
        }, pending ? "…" : "Reveal"),
  );
}


// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────

const _HEADING_STYLES = {
  1: { fontSize: "18px", fontWeight: 700, color: C.navy, marginTop: "18px", marginBottom: "8px",
       fontFamily: "'Playfair Display', serif" },
  2: { fontSize: "15px", fontWeight: 700, color: C.navy, marginTop: "14px", marginBottom: "6px" },
  3: { fontSize: "13px", fontWeight: 700, color: C.navy, marginTop: "10px", marginBottom: "4px",
       textTransform: "uppercase", letterSpacing: "0.04em" },
  4: { fontSize: "12px", fontWeight: 700, color: C.textDark, marginTop: "8px", marginBottom: "4px" },
};

export function SopBodyRenderer({ body, matterId = null, showNotification }) {
  const stripped = _stripFrontMatter(body || "");
  if (!stripped) {
    return React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, fontStyle: "italic" }
    }, "(empty)");
  }

  const ctx = { matterId, showNotification };
  const lines = stripped.split("\n");
  const elements = [];
  let i = 0;
  let key = 0;
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    const joined = paragraphBuffer.join("\n");
    elements.push(React.createElement("p", {
      key: `p${key++}`,
      style: { margin: "0 0 8px 0", fontSize: "13px", color: C.textDark,
               lineHeight: 1.55, whiteSpace: "pre-wrap" },
    }, ..._parseInline(joined, ctx)));
    paragraphBuffer = [];
  };

  while (i < lines.length) {
    const line = lines[i];
    const c = _classifyLine(line);

    if (c.kind === "blank") {
      flushParagraph();
      i++;
      continue;
    }

    if (c.kind === "heading") {
      flushParagraph();
      const style = _HEADING_STYLES[c.level] || _HEADING_STYLES[4];
      const tag = `h${Math.min(c.level, 4)}`;
      elements.push(React.createElement(tag, { key: `h${key++}`, style },
        ..._parseInline(c.content, ctx)));
      i++;
      continue;
    }

    if (c.kind === "bullet" || c.kind === "numbered") {
      // Collect consecutive list items of the same kind
      flushParagraph();
      const items = [];
      const listKind = c.kind;
      while (i < lines.length) {
        const cc = _classifyLine(lines[i]);
        if (cc.kind !== listKind) break;
        items.push(cc);
        i++;
      }
      const tag = listKind === "bullet" ? "ul" : "ol";
      elements.push(React.createElement(tag, {
        key: `l${key++}`,
        style: { margin: "0 0 8px 0", paddingLeft: "22px",
                 fontSize: "13px", color: C.textDark, lineHeight: 1.55 },
      },
        ...items.map((it, idx) => React.createElement("li", {
          key: `li${idx}`,
          style: { marginBottom: "3px" },
        }, ..._parseInline(it.content, ctx))),
      ));
      continue;
    }

    // Regular line — accumulate into a paragraph buffer, so consecutive
    // regular lines join into one paragraph with newlines preserved
    // (pre-wrap on the <p>). Blank lines flush.
    paragraphBuffer.push(c.content);
    i++;
  }
  flushParagraph();

  return React.createElement("div", { style: _bodyContainerStyle }, ...elements);
}

const _bodyContainerStyle = {
  fontSize: "13px", color: C.textDark, lineHeight: 1.55,
  wordWrap: "break-word",
  fontFamily: "system-ui, -apple-system, sans-serif",
};
